import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { FileText } from 'lucide-react';

export default function Terms() {
  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  return (
    <div className="terms-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --accent-primary: #D97642;
          --border-primary: rgba(212, 196, 168, 0.15);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --accent-primary: #D97642;
          --border-primary: rgba(26, 22, 18, 0.15);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .terms-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .terms-content {
          flex: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          width: 100%;
        }

        .terms-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .update-date {
          font-size: 0.9375rem;
          color: var(--text-secondary);
          margin-bottom: 3rem;
        }

        .section {
          margin-bottom: 2.5rem;
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.375rem;
          margin-bottom: 1rem;
        }

        .section-text {
          font-size: 1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }

        ul {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }

        li {
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 0.5rem;
        }

        a {
          color: var(--accent-primary);
        }

        @media (max-width: 768px) {
          .terms-content {
            padding: 2rem 1.5rem;
          }

          .terms-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.125rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="terms-content">
        <h1 className="terms-title">
          <FileText size={32} />
          Terms of Service
        </h1>
        <p className="update-date">Last updated: May 7, 2026</p>

        <div className="section">
          <h2 className="section-title">Acceptance of Terms</h2>
          <p className="section-text">
            These Terms of Service govern your access to and use of MIZAR, operated by TravelTxter Ltd. By accessing the website, console, API, pilot materials, or related services, you agree to these Terms.
          </p>
          <p className="section-text">
            If you are using MIZAR on behalf of a company or organisation, you confirm that you have authority to bind that organisation to these Terms.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Description of Service</h2>
          <p className="section-text">
            MIZAR is a B2B flight price prediction API. The service returns a RegretRisk score, recommendation, confidence metadata, and related response information based on submitted route, date, fare, cabin, and trip context.
          </p>
          <p className="section-text">
            MIZAR does not modify fares, process payments, issue tickets, replace booking engines, or guarantee any specific fare movement or commercial outcome.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">API Access and Use</h2>
          <p className="section-text">You agree to:</p>
          <ul>
            <li>Use API keys only for authorised business purposes.</li>
            <li>Keep API keys secure and confidential.</li>
            <li>Comply with any agreed route, usage, rate limit, pilot, or commercial scope.</li>
            <li>Notify us promptly of suspected unauthorised access or misuse.</li>
            <li>Ensure your use of MIZAR complies with applicable laws and your own customer obligations.</li>
          </ul>
        </div>

        <div className="section">
          <h2 className="section-title">Shadow Mode and Pilot Use</h2>
          <p className="section-text">
            During Shadow Mode or pilot deployments, MIZAR may be used alongside your existing booking flow without displaying customer-facing signals. You are responsible for deciding whether, when, and how any MIZAR output is displayed to your users.
          </p>
          <p className="section-text">
            Unless separately agreed in writing, pilot use is for evaluation purposes only and does not guarantee production availability, commercial uplift, or model performance beyond the validated evidence available at the time.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Acceptable Use</h2>
          <p className="section-text">You must not:</p>
          <ul>
            <li>Use MIZAR for unlawful, misleading, abusive, or unauthorised purposes.</li>
            <li>Send unnecessary personal data, payment data, passport data, or special category data to the API.</li>
            <li>Attempt to reverse engineer, copy, scrape, decompile, or bypass any part of the service.</li>
            <li>Interfere with service availability, security, monitoring, or rate limits.</li>
            <li>Resell, sublicense, or redistribute MIZAR outputs without written permission.</li>
            <li>Present MIZAR outputs to customers in a way that is deceptive, coercive, or inconsistent with consumer protection requirements.</li>
          </ul>
        </div>

        <div className="section">
          <h2 className="section-title">Client Responsibility</h2>
          <p className="section-text">
            You remain responsible for your booking flow, customer communications, pricing decisions, commercial decisions, regulatory compliance, and any use of MIZAR outputs within your own products or services.
          </p>
          <p className="section-text">
            MIZAR provides decision-support information. It does not replace your own commercial judgement, customer service obligations, or legal responsibilities.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Prediction Accuracy and Limitations</h2>
          <p className="section-text">
            MIZAR predictions are probabilistic. Flight prices are affected by airline inventory, demand, supply, currency, fuel prices, route disruption, operational changes, and other external factors outside our control.
          </p>
          <p className="section-text">
            We aim to provide accurate and useful signals, but we do not guarantee that any fare will rise, fall, remain available, or produce a specific booking or revenue outcome.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Fees and Billing</h2>
          <p className="section-text">
            Fees, pilot pricing, usage limits, payment terms, and billing arrangements will be set out in the applicable service agreement, order form, invoice, or commercial proposal.
          </p>
          <p className="section-text">
            Unless otherwise agreed in writing, fees are exclusive of VAT and any applicable taxes.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Intellectual Property</h2>
          <p className="section-text">
            MIZAR, including its models, code, documentation, scoring logic, branding, content, API design, and related intellectual property, is owned by TravelTxter Ltd or its licensors.
          </p>
          <p className="section-text">
            You retain ownership of data you submit to MIZAR, subject to the rights necessary for us to provide, secure, monitor, validate, and improve the service.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Confidentiality</h2>
          <p className="section-text">
            Pilot materials, API credentials, technical documentation, pricing proposals, commercial reports, and non-public performance information may be confidential. You agree not to disclose confidential information except as permitted by written agreement or required by law.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Service Availability and Changes</h2>
          <p className="section-text">
            We may update, improve, suspend, or modify the service from time to time. We will use reasonable efforts to provide notice of material changes that affect active integrations.
          </p>
          <p className="section-text">
            We do not guarantee uninterrupted or error-free availability unless a separate written service level agreement applies.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Suspension and Termination</h2>
          <p className="section-text">
            We may suspend or terminate access if you breach these Terms, exceed agreed usage limits, fail to pay applicable fees, misuse the service, compromise security, or create legal, operational, or reputational risk.
          </p>
          <p className="section-text">
            On termination, your right to access and use MIZAR will cease, but provisions relating to confidentiality, intellectual property, payment, liability, and legal compliance will continue where appropriate.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Limitation of Liability</h2>
          <p className="section-text">
            To the fullest extent permitted by law, TravelTxter Ltd will not be liable for indirect, consequential, special, incidental, exemplary, or punitive losses, including loss of profit, revenue, business opportunity, goodwill, data, or anticipated savings arising from use of MIZAR.
          </p>
          <p className="section-text">
            Nothing in these Terms excludes liability that cannot be excluded under applicable law, including liability for death or personal injury caused by negligence, fraud, or fraudulent misrepresentation.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Indemnity</h2>
          <p className="section-text">
            You agree to indemnify and hold TravelTxter Ltd harmless from claims, losses, liabilities, damages, costs, and expenses arising from your misuse of the service, breach of these Terms, unlawful use of MIZAR outputs, or integration of MIZAR into your own products or customer journeys.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Governing Law</h2>
          <p className="section-text">
            These Terms are governed by the laws of England and Wales. The courts of England and Wales will have exclusive jurisdiction over disputes arising from or relating to these Terms, unless otherwise agreed in writing.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Changes to These Terms</h2>
          <p className="section-text">
            We may update these Terms from time to time. The updated version will be posted on this page with a revised “Last updated” date. Continued use of MIZAR after changes take effect means you accept the updated Terms.
          </p>
        </div>

        <div className="section">
          <h2 className="section-title">Contact Information</h2>
          <p className="section-text">
            For questions about these Terms, contact us at:{' '}
            <a href="mailto:richard.king@traveltxter.com">richard.king@traveltxter.com</a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}